import Avatar from '@orn/shared-web/components/Avatar';
import CloseButton from '@orn/shared-web/components/Button/CloseButton';
import { RecurringIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import { getInitial } from '@orn/shared-web/utils/person/getInitials';
import * as React from 'react';
import { Link } from 'react-router-dom';

export interface IRecordHeroMetaCell {
  /** Small uppercase caption above the value ("Date", "Account"). */
  label: string;
  value: React.ReactNode;
  /** 18px glyph drawn inside the translucent tile. Ignored when `leading` is set. */
  icon?: React.ReactNode;
  /** A 36px node (e.g. a square bank avatar) rendered in place of the icon tile. */
  leading?: React.ReactNode;
  /** Makes the cell a link there (the account's page). */
  to?: string;
}

interface IRecordHeroProps {
  /** Pill in the top-left corner ("Pending", "Expected in 4 days", "Paid"). */
  statusPill?: React.ReactNode;
  /** Logo for the 112px avatar. `null` falls back to the initial of `name`. */
  logoSrc: string | null;
  name: string;
  /** Makes the avatar and name a link there (the merchant's page). */
  to?: string;
  /** Draws the recurring badge over the avatar's corner. */
  showRecurringBadge?: boolean;
  /** Already formatted — a single amount ("$6.50") or a range ("$120 – $165"). */
  amount: string;
  /** Muted line under the amount ("Pending"). */
  caption?: React.ReactNode;
  meta?: IRecordHeroMetaCell[];
  onClose: () => void;
  className?: string;
}

/** The hover surface and focus ring of a link on the hero's dark ground. */
const heroLinkClassName =
  'hover:bg-alpha-white-10 outline-focus-ring transition duration-100 ease-linear focus-visible:outline-2 focus-visible:outline-offset-2';

/**
 * The dark header of a record page (Figma "Card header"): status pill and
 * close button on the top row, then avatar, name, amount, an optional caption,
 * and a row of labeled meta cells. Single-column at every width; the amount
 * steps down one display size below `md` so a range still fits. The avatar
 * and name, and any meta cell, become a link when given a `to`.
 *
 * Padded 20px extra at the bottom so the body's rounded top can overlap it
 * (`RecordPane` pulls the body up by the same amount).
 */
const RecordHero = ({
  statusPill,
  logoSrc,
  name,
  to,
  showRecurringBadge = false,
  amount,
  caption,
  meta = [],
  onClose,
  className,
}: IRecordHeroProps) => {
  return (
    <div className={cx('bg-utility-brand-850 flex w-full flex-col items-center pb-10', className)}>
      <RecordHeroTopRow statusPill={statusPill} onClose={onClose} />

      <RecordHeroIdentity to={to} name={name}>
        <div className="relative">
          <Avatar size="4xl" shape="square" src={logoSrc} alt={name} initials={getInitial(name)} />
          {showRecurringBadge && (
            <div
              data-testid="record-hero-recurring-badge"
              className="bg-utility-brand-700 border-utility-brand-850 absolute -bottom-2 -right-2 flex size-[26px] items-center justify-center rounded-full border-2"
            >
              <RecurringIcon aria-label="Recurring" className="size-[15px] text-white" />
            </div>
          )}
        </div>
        <div className="text-display-sm text-primary_on-brand font-display max-w-full text-center font-medium tracking-[-0.6px]">
          {name}
        </div>
      </RecordHeroIdentity>

      <div className="flex w-full flex-col items-center gap-1 px-5 py-6">
        <div className="text-display-xl md:text-display-2xl text-primary_on-brand font-display text-center font-medium">
          {amount}
        </div>
        {caption && <div className="text-alpha-white-40 text-xl font-medium">{caption}</div>}
      </div>

      {meta.length > 0 && (
        <div className="flex w-full flex-col items-center justify-center gap-3 px-5 py-5 sm:flex-row sm:items-start sm:gap-5">
          {meta.map((cell, index) => (
            <React.Fragment key={`${index}-${cell.label}`}>
              {index > 0 && <div className="bg-alpha-white-10 hidden w-px self-stretch sm:block" aria-hidden="true" />}
              <RecordHeroMetaCell cell={cell} />
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

interface IRecordHeroIdentityProps {
  to?: string;
  name: string;
  children: React.ReactNode;
}

/**
 * The avatar-and-name block, as a link when the record has a page of its own.
 * The link pads out under its hover surface and pulls back in by the same
 * amount, so the block sits where the plain one does. Its accessible name is
 * `name` alone; the avatar's alt would otherwise repeat it.
 */
const RecordHeroIdentity = ({ to, name, children }: IRecordHeroIdentityProps) => {
  const className = 'flex flex-col items-center gap-2';
  if (!to) {
    return <div className={cx(className, 'max-w-full px-5')}>{children}</div>;
  }
  // The wrapper is sized from the link's margin box, so a max-width on the
  // link itself would clamp it 24px short of its content and shift it left.
  return (
    <div className="flex max-w-full px-5">
      <Link to={to} aria-label={name} className={cx(className, '-m-3 rounded-2xl p-3', heroLinkClassName)}>
        {children}
      </Link>
    </div>
  );
};

interface IRecordHeroTopRowProps {
  statusPill?: React.ReactNode;
  onClose: () => void;
}

/**
 * The hero's top row on its own — the loading and error states of
 * `RecordPane` keep the close button reachable without the rest of the hero.
 */
export const RecordHeroTopRow = ({ statusPill, onClose }: IRecordHeroTopRowProps) => {
  return (
    <div className="flex w-full items-center justify-between gap-2 pb-1 pl-5 pr-2 pt-4">
      <div className="flex min-w-0 flex-1 items-start">{statusPill}</div>
      <CloseButton theme="dark" size="md" onPress={onClose} />
    </div>
  );
};

interface IRecordHeroMetaCellProps {
  cell: IRecordHeroMetaCell;
}

const RecordHeroMetaCell = ({ cell }: IRecordHeroMetaCellProps) => {
  const className = 'flex min-w-0 max-w-full items-center gap-2';
  const content = (
    <>
      {cell.leading ? (
        // The avatar repeats the value, so it stays out of the cell's accessible name.
        <div className="shrink-0" aria-hidden="true">
          {cell.leading}
        </div>
      ) : (
        <div className="bg-alpha-white-10 flex size-9 shrink-0 items-center justify-center rounded-sm">
          <span className="flex size-[18px] items-center justify-center text-white [&>svg]:size-full">{cell.icon}</span>
        </div>
      )}
      <div className="flex min-w-0 flex-col">
        <div className="text-2xs text-alpha-white-40 font-medium uppercase tracking-[0.4px]">{cell.label}</div>
        <div className="text-primary_on-brand truncate text-lg font-medium leading-6">{cell.value}</div>
      </div>
    </>
  );

  if (!cell.to) {
    return <div className={className}>{content}</div>;
  }
  return (
    <Link to={cell.to} className={cx(className, '-m-1.5 rounded-lg p-1.5', heroLinkClassName)}>
      {content}
    </Link>
  );
};

export default RecordHero;
